import React from 'react';
import { VideoFeatureItem, FeatureItemProps } from './VideoFeatureItem';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@/lib/utils';

interface SortableVideoFeatureItemProps {
  item: Omit<FeatureItemProps, 'onClick'> & {
    id: string;
    isConfigured?: boolean;
    hasActiveAutoplayTest?: boolean;
    hasActiveAutomaticSpeedTest?: boolean;
    hasActiveManualSpeedTest?: boolean;
    hasActiveHeadlineTest?: boolean;
  };
  onFeatureClick: (featureId: string) => void;
  isOrganizeMode?: boolean;
  isSelected?: boolean;
  isHovered?: boolean;
  isDragging?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export const SortableVideoFeatureItem: React.FC<SortableVideoFeatureItemProps> = ({
  item,
  onFeatureClick,
  isOrganizeMode = false,
  isSelected = false,
  isHovered = false,
  isDragging = false,
  onMouseEnter,
  onMouseLeave,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: sortableDragging
  } = useSortable({ 
    id: item.id,
    disabled: !isOrganizeMode
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Combine dragging states
  const combinedDragging = sortableDragging || isDragging;

  return (
    <li
      ref={setNodeRef}
      style={{
        ...style,
        touchAction: isOrganizeMode ? 'none' : 'auto'
      }}
      role="button"
      tabIndex={0}
      aria-selected={isSelected}
      aria-grabbed={combinedDragging}
      className={cn(
        // Base - Clean & Compact Style
        "flex items-center gap-2.5 h-[36px] px-3 rounded-lg transition-all duration-[160ms] select-none group relative",
        "cursor-pointer text-sm",
        
        // Focus somente teclado
        "focus:outline-none focus:shadow-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(45,108,246,0.25)]",
        
        // Visual states - Clean (sem bordas)
        combinedDragging 
          ? "bg-[rgba(45,108,246,0.08)]"
          : isSelected
            ? "bg-[rgba(45,108,246,0.06)] hover:bg-[rgba(45,108,246,0.06)]"
            : "hover:bg-[rgba(45,108,246,0.04)]",
        
        isOrganizeMode ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={!isOrganizeMode ? () => onFeatureClick(item.id) : undefined}
      onKeyDown={(e) => {
        if (!isOrganizeMode && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onFeatureClick(item.id);
        }
      }}
      {...(isOrganizeMode ? { ...attributes, ...listeners } : {})}
    >
      {/* Linha Lateral Premium - Somente no selecionado */}
      {isSelected && (
        <span 
          className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-[20px] rounded-full bg-[#2D6CF6]"
        />
      )}
      {/* Organize Mode Handle */}
      {isOrganizeMode && (
        <span 
          className={cn(
            "mr-1 cursor-grab active:cursor-grabbing select-none text-lg transition-colors",
            combinedDragging 
              ? "text-blue-600" 
              : isSelected || isHovered 
                ? "text-white/90" 
                : "text-blue-600 group-hover:text-white/90"
          )}
          style={{ touchAction: 'none' }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          ⋮⋮
        </span>
      )}

      {/* Icon Container */}
      <span
        className={cn(
          "w-5 h-5 flex items-center justify-center flex-shrink-0 transition-all duration-200",
          combinedDragging
            ? "text-[#2D6CF6] scale-110"
            : item.hasActiveAutoplayTest || item.hasActiveAutomaticSpeedTest || item.hasActiveHeadlineTest
              ? "text-[#D97706] animate-pulse-alert" // Cor mostarda com animação de pulsar para teste automático ativo
              : item.hasActiveManualSpeedTest || item.isConfigured
                ? "text-[#2D6CF6]" // Azul para teste manual ou configurado
                : "text-[#7176A9] opacity-80"
        )}
        style={{
          strokeWidth: '2.4px'
        }}
      >
        {item.icon}
      </span>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={cn(
          "text-[14px] font-medium leading-tight transition-colors",
          combinedDragging
            ? "text-[#6366F1]"
            : item.hasActiveAutoplayTest || item.hasActiveAutomaticSpeedTest || item.hasActiveHeadlineTest
              ? "text-[#D97706] animate-pulse-alert" // Cor mostarda com animação de pulsar para teste automático ativo
              : item.hasActiveManualSpeedTest || item.isConfigured
                ? "text-[#2D6CF6]" // Azul para teste manual ou configurado
                : "text-[#6A6F85]"
        )}>
          {item.name}
        </p>
      </div>
    </li>
  );
};